// ✅ src/pages/VehicleV2/VehicleTdsPercentEntry.jsx  (FULL FILE)
// Backend mount:
//   app.use("/api/tdsPercent", tdsPercent);
//
// APIs used:
//   GET  https://nissi-india.com/api/tdsPercent/config?branchActive=1
//   GET  https://nissi-india.com/api/tdsPercent/vehicle-types?mode=add|edit
//   GET  https://nissi-india.com/api/tdsPercent/view?tdsId=123
//   POST https://nissi-india.com/api/tdsPercent/save
//
// Routing:
//   Add : /vehicle/VehicleTDSPercent
//   Edit: /vehicle/VehicleTDSPercent?tdsPercentId=123
//
// ✅ Updated to match VehicleFuelRate pattern:
// - common localStorage/sessionStorage header pattern
// - add/edit mode supported
// - loading + pageLoading states
// - branch active / submit control respected
// - same UI structure style
// - back button goes to /vehicle/VehicleTdsPercentListing

import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaArrowLeft, FaSave, FaUndo } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/tdsPercent";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === undefined || v === null) return def;
  return String(v).trim();
};

const pad2 = (n) => String(n).padStart(2, "0");

const ddmmyyyyToYmd = (ddmmyyyy) => {
  const v = toStr(ddmmyyyy);
  const m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
};

const ymdToDDMMYYYY = (ymd) => {
  const v = toStr(ymd);
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
};

const todayToYMD = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const todayToDDMMYYYY = () => {
  const d = new Date();
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const isValidDDMMYYYY = (s) => {
  const v = toStr(s);
  const m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return false;

  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const yyyy = Number(m[3]);

  const d = new Date(yyyy, mm - 1, dd);
  return d.getFullYear() === yyyy && d.getMonth() === mm - 1 && d.getDate() === dd;
};

const clampTds = (raw) => {
  let v = toStr(raw).replace(/[^0-9.]/g, "");
  const parts = v.split(".");
  if (parts.length > 2) v = `${parts[0]}.${parts.slice(1).join("")}`;

  const [a, b] = v.split(".");
  if (typeof b === "string") v = `${a}.${b.slice(0, 2)}`;

  if (v.length > 6) v = v.slice(0, 6);
  return v;
};

const VehicleTdsPercentEntry = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ Shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || localStorage.getItem("branchid") || "";
  const branch = localStorage.getItem("branchType") || localStorage.getItem("branchtype") || "";
  const userId = localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const tdsPercentId = useMemo(() => {
    return toInt(searchParams.get("tdsPercentId") || searchParams.get("tdsId"), 0);
  }, [searchParams]);

  const todayYmd = useMemo(() => todayToYMD(), []);

  const branchActive = useMemo(() => {
    const v = sessionStorage.getItem("BranchActive") || localStorage.getItem("BranchActive") || localStorage.getItem("branchactive");
    if (v === null) return true;
    return v === "1" || v === "true";
  }, []);

  // fields
  const [vehicleSubtypeId, setVehicleSubtypeId] = useState("Select");
  const [dateStr, setDateStr] = useState("");
  const [tdsPercent, setTdsPercent] = useState("");

  // dropdown
  const [vehicleTypes, setVehicleTypes] = useState([]);

  // controls
  const [showSubmit, setShowSubmit] = useState(true);

  // loading
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const inputClass = `w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  /* ------------------------- LOAD CONFIG ------------------------- */
  const loadConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/config`, {
        params: {
          branchActive: branchActive ? 1 : 0,
        },
        headers: commonHeaders,
      });

      setShowSubmit(Boolean(data?.showSubmit ?? true));
    } catch (err) {
      console.error(err);
      setShowSubmit(true);
    }
  }, [branchActive, commonHeaders]);

  /* ------------------------- LOAD VEHICLE TYPES ------------------------- */
  const loadVehicleTypes = useCallback(async () => {
    try {
      const mode = tdsPercentId > 0 ? "edit" : "add";

      const { data } = await axios.get(`${API_BASE}/vehicle-types`, {
        params: { mode },
        headers: commonHeaders,
      });

      const list = Array.isArray(data?.vehicleTypes) ? data.vehicleTypes : [];

      setVehicleTypes(
        [{ id: "Select", name: "Select" }].concat(
          list.map((x) => ({
            id: String(x.id),
            name: String(x.name),
          }))
        )
      );
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load vehicle types");
      setVehicleTypes([{ id: "Select", name: "Select" }]);
    }
  }, [tdsPercentId, commonHeaders]);

  /* ------------------------- LOAD EDIT DATA ------------------------- */
  const loadEditData = useCallback(async () => {
    if (!tdsPercentId) return;

    try {
      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/view`, {
        params: { tdsId: tdsPercentId },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to load TDS percent");
      }

      const d = data?.data || {};

      setVehicleSubtypeId(d.subtypeId != null ? String(d.subtypeId) : "Select");
      setDateStr(toStr(d.date));
      setTdsPercent(d.tdsPercent != null ? String(d.tdsPercent) : "");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Failed to load TDS percent");
    } finally {
      setPageLoading(false);
    }
  }, [tdsPercentId, commonHeaders]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    loadVehicleTypes();
  }, [loadVehicleTypes]);

  useEffect(() => {
    if (tdsPercentId > 0) {
      loadEditData();
    } else {
      setDateStr(todayToDDMMYYYY());
    }
  }, [tdsPercentId, loadEditData]);

  const canSave = useMemo(() => {
    if (!branchActive || !showSubmit) return false;
    if (String(vehicleSubtypeId) === "Select") return false;
    if (!toStr(dateStr)) return false;
    if (!toStr(tdsPercent)) return false;
    return true;
  }, [vehicleSubtypeId, dateStr, tdsPercent, branchActive, showSubmit]);

  const validate = () => {
    if (!branchActive) {
      toast.error("Branch is not active. Save not allowed.");
      return false;
    }

    if (!showSubmit) {
      toast.error("Submit is disabled.");
      return false;
    }

    if (String(vehicleSubtypeId) === "Select") {
      toast.error("Please select vehicle type");
      return false;
    }

    if (!toStr(dateStr)) {
      toast.error("Please select date");
      return false;
    }

    if (!isValidDDMMYYYY(dateStr)) {
      toast.error("Invalid date format (dd/MM/yyyy)");
      return false;
    }

    const [dd, mm, yyyy] = dateStr.split("/").map(Number);
    const selectedDate = new Date(yyyy, mm - 1, dd);
    selectedDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate.getTime() > today.getTime()) {
      toast.error("Select Current Date or Previous date");
      return false;
    }

    if (!toStr(tdsPercent)) {
      toast.error("Please enter TDS Percentage");
      return false;
    }

    const percentNum = Number(tdsPercent);
    if (!Number.isFinite(percentNum)) {
      toast.error("Please enter valid TDS Percentage");
      return false;
    }

    if (percentNum <= 0) {
      toast.error("Please enter TDS Percentage greater than 0");
      return false;
    }

    if (percentNum > 100) {
      toast.error("Please enter TDS Percentage less than or equal to 100");
      return false;
    }

    return true;
  };

  const handleClear = () => {
    if (tdsPercentId > 0) {
      loadEditData();
      return;
    }

    setVehicleSubtypeId("Select");
    setDateStr(todayToDDMMYYYY());
    setTdsPercent("");
  };

  const handleSave = async () => {
    if (!validate()) return;

    const payload = {
      tdsId: tdsPercentId || 0,
      vehicleSubtypeId: toInt(vehicleSubtypeId, 0),
      date: toStr(dateStr), // dd/MM/yyyy
      tdsPercent: Number(tdsPercent),
      loginUserId,
      branchId,
      branchType: branch,
      branchActive: branchActive ? 1 : 0,
    };

    try {
      setLoading(true);

      const { data } = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to save TDS percent");
      }

      toast.success(
        data?.message ||
          (tdsPercentId ? "TDS percent updated successfully" : "TDS percent saved successfully")
      );

      setTimeout(() => {
        navigate("/vehicle/VehicleTdsPercentListing");
      }, 800);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Failed to save TDS percent");
    } finally {
      setLoading(false);
    }
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE TDS PERCENT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black`
          }`}
        >
          {/* Header */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-8">
            <div className="flex items-center gap-4 flex-wrap">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                {tdsPercentId ? "Edit Vehicle TDS Percent" : "Add Vehicle TDS Percent"}
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  tdsPercentId
                    ? isDark
                      ? "bg-yellow-900 text-yellow-100"
                      : "bg-yellow-100 text-yellow-800"
                    : isDark
                    ? "bg-blue-900 text-blue-100"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {tdsPercentId ? `Edit Mode (#${tdsPercentId})` : "New Entry"}
              </span>
            </div>

            <button
              type="button"
              onClick={() => navigate("/vehicle/VehicleTdsPercentListing")}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-800"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <FaArrowLeft className="w-4 h-4" />
              Back to Listing
            </button>
          </div>

          {/* Form */}
          <div
            className={`rounded-xl p-6 border ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="max-w-2xl mx-auto">
              {/* Vehicle Type */}
              <div className="mb-8">
                <label className={labelClass}>
                  Vehicle Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="vehicleSubtypeId"
                  value={vehicleSubtypeId}
                  onChange={(e) => setVehicleSubtypeId(e.target.value)}
                  className={inputClass}
                  disabled={pageLoading || loading || tdsPercentId > 0}
                >
                  {vehicleTypes.map((x) => (
                    <option key={x.id} value={String(x.id)}>
                      {x.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date */}
              <div className="mb-8">
                <label className={labelClass}>
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  id="date"
                  type="date"
                  value={ddmmyyyyToYmd(dateStr)}
                  max={todayYmd}
                  onChange={(e) => setDateStr(ymdToDDMMYYYY(e.target.value))}
                  className={inputClass}
                  disabled={pageLoading || loading}
                />
              </div>

              {/* TDS Percent */}
              <div className="mb-8">
                <label className={labelClass}>
                  TDS Percent <span className="text-red-500">*</span>
                </label>
                <input
                  id="tdsPercent"
                  type="text"
                  value={tdsPercent}
                  onChange={(e) => setTdsPercent(clampTds(e.target.value))}
                  onBlur={() => setTdsPercent(clampTds(tdsPercent))}
                  placeholder="Enter TDS percent..."
                  className={inputClass}
                  disabled={pageLoading || loading}
                  inputMode="decimal"
                  maxLength={6}
                />
              </div>

              {/* Info */}
              <div className="mb-8">
                <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Date must be current or past date. TDS percent should be greater than 0 and less than or equal to 100.
                </div>
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={handleClear}
                  disabled={loading || pageLoading}
                  className="px-6 py-3 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <FaUndo className="w-4 h-4" />
                  {tdsPercentId ? "Reload" : "Clear"}
                </button>

                <button
                  type="button"
                  onClick={handleSave}
                  disabled={loading || pageLoading || !canSave}
                  className={`flex items-center gap-2 px-8 py-3 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    loading || pageLoading || !canSave
                      ? "bg-gray-400 cursor-not-allowed"
                      : `${buttonClass} ${hoverClass}`
                  }`}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <FaSave className="w-4 h-4" />
                      {tdsPercentId ? "Update TDS Percent" : "Save TDS Percent"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className={`mt-8 pt-6 border-t ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <p className={`text-sm text-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              INFRA SCREEN : VEHICLE TDS PERCENT
            </p>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleTdsPercentEntry;